import { useState } from 'react';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from '../../../components/ui/card';
import { DataTable, DataTableColumn } from '../../../components/ui/data-table';
import { Button } from '../../../components/ui/button';
import PageHeader from '../../../components/common/PageHeader';
import { Check, X, Eye, AlertCircle } from 'lucide-react';

type Strategy = {
  id: string;
  name: string;
  provider: string;
  type: 'copy_trading' | 'pamm' | 'mam';
  description: string;
  roi: number;
  riskLevel: 'low' | 'medium' | 'high';
  followers: number;
  submittedDate: string;
  status: 'pending' | 'approved' | 'rejected' | 'suspended';
};

const AdminStrategyApproval = () => {
  const [statusFilter, setStatusFilter] = useState<
    'all' | 'pending' | 'approved' | 'rejected' | 'suspended'
  >('pending');

  const strategies: Strategy[] = [
    {
      id: 'STR001',
      name: 'Gold Scalper Pro',
      provider: 'John Smith',
      type: 'copy_trading',
      description:
        'High-frequency gold trading strategy with tight risk management',
      roi: 45.2,
      riskLevel: 'high',
      followers: 0,
      submittedDate: '2024-01-15',
      status: 'pending',
    },
    {
      id: 'STR002',
      name: 'Alpha Growth Fund',
      provider: 'Mike Johnson',
      type: 'pamm',
      description: 'Long-term growth fund focusing on major currency pairs',
      roi: 32.8,
      riskLevel: 'medium',
      followers: 0,
      submittedDate: '2024-01-14',
      status: 'pending',
    },
    {
      id: 'STR003',
      name: 'Forex Trend Master',
      provider: 'Sarah Williams',
      type: 'copy_trading',
      description: 'Trend-following strategy for EURUSD and GBPUSD',
      roi: 28.5,
      riskLevel: 'low',
      followers: 0,
      submittedDate: '2024-01-13',
      status: 'pending',
    },
    {
      id: 'STR004',
      name: 'Conservative Income',
      provider: 'David Brown',
      type: 'mam',
      description: 'Low-risk income generation with steady returns',
      roi: 15.3,
      riskLevel: 'low',
      followers: 0,
      submittedDate: '2024-01-12',
      status: 'approved',
    },
    {
      id: 'STR005',
      name: 'Crypto Alpha',
      provider: 'Emily Chen',
      type: 'copy_trading',
      description: 'Cryptocurrency trading strategy with high volatility',
      roi: 67.5,
      riskLevel: 'high',
      followers: 0,
      submittedDate: '2024-01-11',
      status: 'rejected',
    },
  ];

  const filteredStrategies = strategies.filter(
    strategy => statusFilter === 'all' || strategy.status === statusFilter
  );

  const handleApprove = (id: string) => {
    console.log('Approving strategy:', id);
  };

  const handleReject = (id: string) => {
    console.log('Rejecting strategy:', id);
  };

  const handleView = (id: string) => {
    console.log('Viewing strategy:', id);
  };

  const strategyColumns: DataTableColumn<Strategy>[] = [
    {
      id: 'id',
      header: 'ID',
      accessor: row => <span className="font-medium">{row.id}</span>,
      sortable: true,
      sortValue: row => row.id,
      width: '100px',
    },
    {
      id: 'name',
      header: 'Strategy Name',
      accessor: row => (
        <div>
          <div className="font-semibold">{row.name}</div>
          <div className="text-sm text-muted-foreground">{row.provider}</div>
        </div>
      ),
      sortable: true,
      sortValue: row => row.name,
      width: '200px',
    },
    {
      id: 'type',
      header: 'Type',
      accessor: row => (
        <span
          className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
            row.type === 'copy_trading'
              ? 'bg-blue-100 text-blue-800'
              : row.type === 'pamm'
                ? 'bg-purple-100 text-purple-800'
                : 'bg-orange-100 text-orange-800'
          }`}
        >
          {row.type.replace('_', ' ').toUpperCase()}
        </span>
      ),
      sortable: true,
      sortValue: row => row.type,
      width: '120px',
    },
    {
      id: 'roi',
      header: 'ROI',
      accessor: row => (
        <span className="font-semibold text-green-600">+{row.roi}%</span>
      ),
      sortable: true,
      sortValue: row => row.roi,
      width: '100px',
    },
    {
      id: 'riskLevel',
      header: 'Risk Level',
      accessor: row => (
        <span
          className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
            row.riskLevel === 'low'
              ? 'bg-green-100 text-green-800'
              : row.riskLevel === 'medium'
                ? 'bg-yellow-100 text-yellow-800'
                : 'bg-red-100 text-red-800'
          }`}
        >
          {row.riskLevel.toUpperCase()}
        </span>
      ),
      sortable: true,
      sortValue: row => row.riskLevel,
      width: '120px',
    },
    {
      id: 'submittedDate',
      header: 'Submitted',
      accessor: row => new Date(row.submittedDate).toLocaleDateString(),
      sortable: true,
      sortValue: row => row.submittedDate,
      width: '120px',
    },
    {
      id: 'status',
      header: 'Status',
      accessor: row => (
        <span
          className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
            row.status === 'approved'
              ? 'bg-green-100 text-green-800'
              : row.status === 'rejected'
                ? 'bg-red-100 text-red-800'
                : row.status === 'suspended'
                  ? 'bg-orange-100 text-orange-800'
                  : 'bg-yellow-100 text-yellow-800'
          }`}
        >
          {row.status.charAt(0).toUpperCase() + row.status.slice(1)}
        </span>
      ),
      sortable: true,
      sortValue: row => row.status,
      width: '120px',
    },
    {
      id: 'actions',
      header: 'Actions',
      accessor: row => (
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="sm" onClick={() => handleView(row.id)}>
            <Eye className="h-4 w-4" />
          </Button>
          {row.status === 'pending' && (
            <>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => handleApprove(row.id)}
                className="text-green-600 hover:text-green-700"
              >
                <Check className="h-4 w-4" />
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => handleReject(row.id)}
                className="text-red-600 hover:text-red-700"
              >
                <X className="h-4 w-4" />
              </Button>
            </>
          )}
        </div>
      ),
      sortable: false,
      width: '120px',
    },
  ];

  const pendingCount = strategies.filter(s => s.status === 'pending').length;
  const approvedCount = strategies.filter(s => s.status === 'approved').length;
  const rejectedCount = strategies.filter(s => s.status === 'rejected').length;

  return (
    <div className="space-y-6">
      <div className="sticky top-0 z-20 bg-background pt-4">
        <PageHeader
          title="Strategy Approval"
          description="Review and approve trading strategies"
        />
      </div>

      <div className="flex items-center gap-2 flex-wrap">
        <Button
          variant={statusFilter === 'all' ? 'default' : 'outline'}
          size="sm"
          onClick={() => setStatusFilter('all')}
        >
          All ({strategies.length})
        </Button>
        <Button
          variant={statusFilter === 'pending' ? 'default' : 'outline'}
          size="sm"
          onClick={() => setStatusFilter('pending')}
        >
          Pending ({pendingCount})
        </Button>
        <Button
          variant={statusFilter === 'approved' ? 'default' : 'outline'}
          size="sm"
          onClick={() => setStatusFilter('approved')}
        >
          Approved ({approvedCount})
        </Button>
        <Button
          variant={statusFilter === 'rejected' ? 'default' : 'outline'}
          size="sm"
          onClick={() => setStatusFilter('rejected')}
        >
          Rejected ({rejectedCount})
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <AlertCircle className="h-5 w-5 text-yellow-600" />
              Pending Approval
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold">{pendingCount}</div>
            <p className="text-sm text-muted-foreground mt-2">
              Strategies awaiting review
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Check className="h-5 w-5 text-green-600" />
              Approved
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold">{approvedCount}</div>
            <p className="text-sm text-muted-foreground mt-2">
              Active strategies
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <X className="h-5 w-5 text-red-600" />
              Rejected
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold">{rejectedCount}</div>
            <p className="text-sm text-muted-foreground mt-2">
              Declined strategies
            </p>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Strategy Submissions</CardTitle>
          <CardDescription>
            Review and manage strategy approval requests
          </CardDescription>
        </CardHeader>
        <CardContent>
          <DataTable
            columns={strategyColumns}
            data={filteredStrategies}
            searchPlaceholder="Search strategies..."
            showColumnVisibility={true}
            showExportButton={false}
            showAddButton={false}
          />
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminStrategyApproval;
